export const ACTIVE_UPDATE_JOB_STATUSES = ["queued", "running"];

// Contrato compartilhado (Task 14, pipeline/BE/FE em paralelo, 02/09): 429 do Sienge vira este
// código no job (`error_code`) e no corpo do POST (`{ code, message, retry_after }`); a
// construtora fica com `sienge_quota_blocked_until` (Date UTC) até a próxima renovação da cota.
export const SIENGE_QUOTA_ERROR_CODE = "sienge_quota_exceeded";

// Task 17B: o data-pipeline marca como `failed`/`service_restarted` os `update_jobs` que
// ficaram em `queued`/`running` quando o serviço reinicia (deploy, restart) sem que o pipeline
// chegue a fechá-los sozinho. O usuário não fez nada de errado -- só precisa disparar de novo.
export const SERVICE_RESTARTED_ERROR_CODE = "service_restarted";
export const SERVICE_RESTARTED_MESSAGE = "A atualização foi interrompida por uma reinicialização do serviço. Dispare novamente.";

const UPDATE_TYPES_BY_ERP = {
  SIENGE: [
    "complete",
    "disbursement",
    "trend",
    "financial",
    "purchase_orders",
    "stock",
    "prevision",
  ],
  MEGA: ["complete", "financial", "prevision"],
};
const UPDATE_JOB_TYPES = new Set([
  "complete",
  "disbursement",
  "trend",
  "financial",
  "purchase_orders",
  "stock",
  "prevision",
]);
const UPDATE_CAPABILITY_CONDITIONS = new Set(["available", "read_only", "unavailable"]);

const parseBody = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const request = async (fetchFn, url, options) => {
  try {
    const response = await fetchFn(url, options);
    return {
      ok: response.ok,
      status: response.status,
      body: await parseBody(response),
    };
  } catch {
    return { ok: false, status: 0, body: null };
  }
};

export const requestUpdateJob = ({
  fetchFn,
  serverUrl,
  companyId,
  buildingId,
  type,
  period,
}) => request(fetchFn, `${serverUrl}/project/updateJob`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    company_id: companyId,
    building_id: buildingId,
    type,
    ...(period ? { period } : {}),
  }),
});

export const loadUpdateJob = ({ fetchFn, serverUrl, buildingId }) => request(
  fetchFn,
  `${serverUrl}/project/updateJob?building_id=${encodeURIComponent(buildingId)}`,
  { method: "GET" },
);

export const isUpdateJobActive = (job) => ACTIVE_UPDATE_JOB_STATUSES.includes(job?.status);

export const canUseUpdateType = (erp, type) => (
  UPDATE_TYPES_BY_ERP[String(erp || "").toUpperCase()]?.includes(type) ?? false
);

export const resolveProjectUpdateTypes = (project, erp) => {
  const contract = project?.updateCapabilities;
  if (contract !== undefined && contract !== null) {
    if (
      typeof contract !== "object"
      || Array.isArray(contract)
      || contract.version !== 1
      || !UPDATE_CAPABILITY_CONDITIONS.has(contract.condition)
      || !Array.isArray(contract.types)
    ) return [];
    if (contract.condition !== "available") return [];
    return [...new Set(contract.types.filter((type) => UPDATE_JOB_TYPES.has(type)))];
  }
  return [...(UPDATE_TYPES_BY_ERP[String(erp || "").toUpperCase()] ?? [])];
};

// Só a data (sem hora) usa timeZone: "UTC" no resto do arquivo de datas (`formatDate.js`) porque
// aquelas datas são "dia puro", sem componente de hora real. `sienge_quota_blocked_until` é um
// instante de verdade (o próximo 00:00 em America/Sao_Paulo, gravado em UTC) — o usuário precisa
// ver a hora convertida para o fuso do navegador, não UTC cru.
export const parseSiengeQuotaBlockedUntil = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isSiengeQuotaBlocked = (value, now = new Date()) => {
  const date = parseSiengeQuotaBlockedUntil(value);
  return !!date && date.getTime() > now.getTime();
};

export const formatSiengeQuotaMessage = (value) => {
  const date = parseSiengeQuotaBlockedUntil(value);
  const base = "Cota diária da API Sienge desta construtora foi atingida.";
  if (!date) return base;
  const time = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const day = date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return `${base} Nova atualização disponível a partir de ${time} de ${day}.`;
};

// `quotaBlockedUntil`: o BE (`serializeUpdateJob`) NÃO copia `sienge_quota_blocked_until` para
// dentro do job serializado — o campo só existe no topo do payload de status. Quem chama esta
// função a partir de um job com `error_code` de cota precisa repassar o valor de onde de fato
// veio (ver `Header.jsx`); sem ele a mensagem cai no texto-base, sem "a partir de HH:MM de dd/MM".
export const updateJobMessage = (job, quotaBlockedUntil) => {
  if (job?.error_code === SIENGE_QUOTA_ERROR_CODE) {
    return formatSiengeQuotaMessage(quotaBlockedUntil ?? job?.sienge_quota_blocked_until);
  }
  if (job?.error_code === SERVICE_RESTARTED_ERROR_CODE) {
    return SERVICE_RESTARTED_MESSAGE;
  }
  switch (job?.status) {
    case "queued":
      return "Atualização na fila. Você pode sair desta página; enviaremos um e-mail quando terminar.";
    case "running":
      return "Atualização em andamento. Você pode sair desta página; enviaremos um e-mail quando terminar.";
    case "succeeded":
      return "Atualização concluída. A confirmação também é enviada por e-mail.";
    case "failed":
      return "Não foi possível concluir a atualização. Você receberá a confirmação por e-mail.";
    case "blocked":
      return "Atualização bloqueada. Você receberá a confirmação por e-mail.";
    default:
      return "";
  }
};
